import { DropdownInput, IntegrationData, TextFieldInput } from 'types/mounting'
import { createDlubalSnowButton, createDlubalWindButton } from '../../dlubalButtonConfigs'
import { filterOptions } from '../../inputFilters'
import { RoofTypeName } from '../../types'
import { K2Schema, K2SchemaMap, K2SchemaProperties } from './K2APISchema'
import { generateCompatibilityRules, generateFilterCriteria } from './K2Compatibility'
import { getK2Config } from './K2Config'
import { K2PartsMap } from './K2PartsList'
import { K2MountingSystemName, K2RailType } from './K2Types'

const K2_MOUNTING_NAMES = ['k2OnRoof', 'k2SimpleTilt', 'k2GroundMount'] as const

export function createK2Inputs(k2MountingSystemName: K2MountingSystemName, roofTypeName: RoofTypeName | null) {
  return (projectForm, system) => {
    const integrationData = system.integration_json?.[k2MountingSystemName] as IntegrationData
    const k2RailType = (k2MountingSystemName === 'k2OnRoof' || k2MountingSystemName === 'k2OnRoofUK' ? 'crossrail' : 'simpleTilt') as K2RailType
    const k2Schema = K2SchemaMap[k2RailType]
    const engineeringSchema = getK2Config(k2RailType).engineeringSchema
    return {
      dropdownInputs: k2Dropdowns(k2Schema, k2MountingSystemName, integrationData, roofTypeName).concat(
        k2Dropdowns(engineeringSchema, k2MountingSystemName, integrationData, roofTypeName)
      ),
      textFieldInputs: k2TextFieldInputs(k2Schema).concat(k2TextFieldInputs(engineeringSchema)),
    }
  }
}

function formatDropdownOptionWithDisplayLabel(productCode: string | number, fieldName: string) {
  // First check if it's a component part number
  const componentDescription = K2PartsMap[String(productCode)]
  if (componentDescription) {
    const productCodeString = String(productCode)
    const isValidProductCode = /^(\d+|\d+-[A-Z])$/.test(productCodeString)
    return {
      label: isValidProductCode ? `${componentDescription} (${productCode})` : componentDescription,
      value: productCode,
    }
  }

  // Otherwise check for custom display labels
  const displayLabelMap: Record<string, Record<string, string>> = {
    mountingOption: {
      rafter: 'Rafter',
      deck: 'Deck',
    },
    roofShape: {
      gable: 'Gable',
      monoslope: 'Mono-slope',
      flat: 'Flat',
    },
    exposureCategory: {
      B: 'B (Urban/Suburban)',
      C: 'C (Open Terrain)',
      D: 'D (Flat/Coastal)',
    },
  }

  const fieldLabels = displayLabelMap[fieldName]
  const customLabel = fieldLabels?.[String(productCode)]

  return {
    label: customLabel || String(productCode),
    value: productCode,
  }
}

function validDropdown(
  entry: [string, K2SchemaProperties]
): entry is [string, K2SchemaProperties & { enum: string[]; default: string }] {
  const [fieldName, field] = entry
  const unwantedFieldNames = ['customModuleUnit', 'tiltAngle', 'unitSystem', 'region']
  const fieldNameValid = !unwantedFieldNames.includes(fieldName)
  const dropdownHasOptions = Array.isArray(field?.enum) && field?.enum.length > 0
  return fieldNameValid && dropdownHasOptions
}

function k2Dropdowns(
  k2Schema: K2Schema,
  k2MountingSystemName: K2MountingSystemName,
  integrationData: IntegrationData,
  roofTypeName: RoofTypeName | null
) {
  const compatibilityRules = generateCompatibilityRules(k2MountingSystemName)
  const filterCriteria = generateFilterCriteria(k2MountingSystemName, integrationData, roofTypeName)
  const propertiesToDropdownInput = ([fieldName, fieldProperties]): DropdownInput => ({
    label: k2LabelName(fieldName),
    variableName: fieldName,
    default: fieldProperties.default ?? fieldProperties.enum[0],
    options: fieldProperties.enum?.map((productCode) => formatDropdownOptionWithDisplayLabel(productCode, fieldName)),
  })
  const k2EnumFields = Object.entries(k2Schema.properties).filter(validDropdown).map(propertiesToDropdownInput)
  const inputsToDisplay = filterOptions(k2EnumFields, compatibilityRules, filterCriteria, integrationData)
  return [...inputsToDisplay]
}

function validTextField(
  entry: [string, K2SchemaProperties]
): entry is [string, K2SchemaProperties & { enum: string[]; default: string }] {
  const [fieldName, field] = entry
  const unwantedFieldNames = [
    'modulePerformance',
    'customModuleHeight',
    'customModuleLength',
    'customModuleWidth',
    'enforceBusinessRules',
    'panelLength',
    'reactionForces',
  ]
  const fieldNameValid = !unwantedFieldNames.includes(fieldName)
  return fieldNameValid && !field.enum && !field.$ref && !field.items
}

function k2TextFieldInputs(k2Schema: K2Schema): TextFieldInput[] {
  const propertiesToTextFieldInput = ([fieldName, fieldProperties]): TextFieldInput => {
    const baseInput = {
      label: k2LabelName(fieldName),
      variableName: fieldName,
      type: fieldProperties.type === 'integer' || fieldProperties.type === 'number' ? 'number' : 'string',
      default: fieldProperties.default ?? (fieldProperties.type === 'integer' ? 0 : ''),
    }

    // Add button for wind speed field
    if (fieldName === 'windSpeed') {
      return {
        ...baseInput,
        button: createDlubalWindButton([...K2_MOUNTING_NAMES]),
      }
    }

    // Add button for snow load field
    if (fieldName === 'designSnowLoad') {
      return {
        ...baseInput,
        button: createDlubalSnowButton([...K2_MOUNTING_NAMES]),
      }
    }

    return baseInput
  }

  const k2EnumFields = Object.entries(k2Schema.properties).filter(validTextField).map(propertiesToTextFieldInput)
  return k2EnumFields
}

function k2LabelName(k2FieldName: string) {
  const k2LabelDict: Record<string, string> = {
    pipeSch: 'Schedule (thickness) of pipe',
    railLength: 'Rail Length (inches)',
    railFinish: 'Rail Finish',
    clampFinish: 'Clamp Finish',
    designCode: 'Design Code',
    roofCovering: 'Roof Covering',
    endClamp: 'End Clamp',
    roofHooks: 'Roof Hooks',
    attachmentSpacing: 'Attachment Spacing (inches)',
    attachmentFinish: 'Attachment Finish',
    attachmentType: 'Attachment Type',
    fastener: 'Fastener',
    windSpeed: 'Wind Speed (mph)',
    exposureCategory: 'Exposure Category',
    designSnowLoad: 'Design Snow Load (psf)',
    buildingHeight: 'Building Height (feet)',
    tiltAngle: 'Tilt Angle (degrees)',
    panelLength: 'Panel Length (inches)',
    siteElevation: 'Site Elevation (feet)',
  }
  return k2LabelDict[k2FieldName] || camelCaseToTitleCase(k2FieldName)
}

function camelCaseToTitleCase(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // capitalize the first letter
}
